import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, ClipboardCopy, NotebookPen, RotateCcw } from 'lucide-react';
import { useCaseCreate, useNoteCreate } from '../../../api/hooks';
import type { CoachAction } from '../../../api/types';
import { useAuth } from '../../../../utils/Auth/AuthProvider';

const CARD = 'rounded-[14px] border border-line bg-surface p-3';
const BTN = 'inline-flex min-h-[36px] items-center gap-1.5 rounded-[10px] px-3 text-[13px] font-semibold disabled:opacity-50';
const PRIMARY = `${BTN} bg-navy text-white hover:brightness-110 dark:bg-mist-fixed dark:text-navy`;
const GHOST = `${BTN} border border-line-strong text-ink hover:bg-surface-2`;

/**
 * A step the coach proposed. Nothing happens until the staff member confirms:
 * a note is saved, a case is opened, a parent message is copied, or the class
 * page opens for a remediation cycle.
 */
export default function CoachActionCard({ action, onDismiss }: { action: CoachAction; onDismiss: () => void }) {
  const { role } = useAuth();
  const navigate = useNavigate();
  const note = useNoteCreate();
  const openCase = useCaseCreate();
  const [text, setText] = useState(
    action.type === 'create_note' ? action.body : action.type === 'draft_parent_message' ? action.text : '');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return <div role="status" className={`${CARD} text-[13px] text-success`}>{done}</div>;
  }

  if (action.type === 'create_note') {
    const vis = role === 'teacher' ? 'teachers_of_student' : action.visibility;
    return (
      <div className={CARD} aria-label={`Proposed note about ${action.student_name}`}>
        <p className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          <NotebookPen size={16} strokeWidth={1.8} aria-hidden /> Save a note about {action.student_name}
        </p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} aria-label="Note text"
          className="w-full resize-y rounded-[10px] border border-line bg-surface-2 p-2 text-[13px] text-ink" />
        <p className="mt-1 text-xs text-muted">Visible to {vis === 'counselor_admin_only' ? 'counsellors and admins' : "the student's teachers"}.</p>
        {error && <p role="alert" className="mt-1 text-xs text-danger-ink">{error}</p>}
        <div className="mt-2 flex gap-2">
          <button type="button" className={PRIMARY} disabled={!text.trim() || note.isPending}
            onClick={async () => {
              setError(null);
              try {
                await note.mutateAsync({ student: action.student_id, body: text.trim(), visibility: vis });
                setDone(`Note saved for ${action.student_name}.`);
              } catch { setError('The note could not be saved.'); }
            }}>Save note</button>
          <button type="button" className={GHOST} onClick={onDismiss}>Dismiss</button>
        </div>
      </div>
    );
  }

  if (action.type === 'open_case') {
    const category = role === 'teacher' && action.category === 'wellbeing' ? 'other' : action.category;
    return (
      <div className={CARD} aria-label={`Proposed case for ${action.student_name}`}>
        <p className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
          <Briefcase size={16} strokeWidth={1.8} aria-hidden /> Open a {category} case for {action.student_name}
        </p>
        {action.summary && <p className="text-[13px] text-muted-2">{action.summary}</p>}
        {error && <p role="alert" className="mt-1 text-xs text-danger-ink">{error}</p>}
        <div className="mt-2 flex gap-2">
          <button type="button" className={PRIMARY} disabled={openCase.isPending}
            onClick={async () => {
              setError(null);
              try {
                const made = await openCase.mutateAsync({ student: action.student_id, category });
                navigate(`/cases/${made.id}`);
              } catch { setError('The case could not be opened.'); }
            }}>Open case</button>
          <button type="button" className={GHOST} onClick={onDismiss}>Dismiss</button>
        </div>
      </div>
    );
  }

  if (action.type === 'draft_parent_message') {
    return (
      <div className={CARD} aria-label={`Draft message to ${action.student_name}'s family`}>
        <p className="mb-2 text-[13px] font-semibold text-ink">Draft message to {action.student_name}&apos;s family</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} aria-label="Message text"
          className="w-full resize-y rounded-[10px] border border-line bg-surface-2 p-2 text-[13px] text-ink" />
        <p className="mt-1 text-xs text-muted">Nothing is sent: copy it into your message to the family.</p>
        <div className="mt-2 flex gap-2">
          <button type="button" className={PRIMARY}
            onClick={async () => {
              try { await navigator.clipboard.writeText(text); setDone('Copied. Paste it into your message.'); }
              catch { setError('Copy is blocked here: select the text and copy it.'); }
            }}><ClipboardCopy size={15} strokeWidth={1.8} aria-hidden /> Copy</button>
          <button type="button" className={GHOST} onClick={onDismiss}>Dismiss</button>
        </div>
        {error && <p role="alert" className="mt-1 text-xs text-danger-ink">{error}</p>}
      </div>
    );
  }

  const href = action.section_id ? `/school/section/${action.section_id}` : '/class-performance';
  return (
    <div className={CARD} aria-label="Proposed remediation cycle">
      <p className="mb-1 flex items-center gap-1.5 text-[13px] font-semibold text-ink">
        <RotateCcw size={16} strokeWidth={1.8} aria-hidden /> Start a remediation cycle
      </p>
      {action.chapters.length > 0 && <p className="text-[13px] text-muted-2">On {action.chapters.join(', ')}.</p>}
      <p className="mt-1 text-xs text-muted">Opens the class page; pick the chapters in its Cycles tab.</p>
      <div className="mt-2 flex gap-2">
        <Link to={href} className={PRIMARY}>Open class page</Link>
        <button type="button" className={GHOST} onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  );
}
